import { useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import useLatest from "@-/com.npmjs.use-latest";
import { useReviewRechargeQueryOptions } from "@-/dev.improve.haniverse.lib.cards.review";
import {
  nextDueUserCardDataQueryFn,
  useGetNextDueUserCardDataQueryKey,
} from "@-/dev.improve.haniverse.lib.cards.study-card-data";
import { useStudiedGraphemesQueryOptions } from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.meaning.read";
import { usePronouncedGraphemesQueryOptions } from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.pronunciation.read";
import type { LearnCardDestinationReads } from "@-/dev.improve.haniverse.lib.learn.destination";
import {
  nextLessonQueryFn,
  useGetNextLessonQueryKey,
} from "@-/dev.improve.haniverse.lib.lesson.get";
import { useLevels } from "@-/dev.improve.haniverse.routes.nav.app._lib.level";
import { useMemo } from "react";

/**
 * The resolver's reads, bound to the query cache.
 *
 * Every read is a `fetchQuery` over the REGISTERED key for that decision — the same keys the
 * reactive screens observe — so the write-clock freshness guard applies: a read issued moments
 * after a rating commit cannot be served a pre-commit snapshot, and a cached entry is reused when
 * it is provably current. Key getters are read through refs so the returned object is stable
 * across renders and safe to capture in a long-lived controller.
 */
export function useLearnCardDestinationReads(): LearnCardDestinationReads {
  const queryClient = useQueryClient();
  const getNextDueUserCardDataQueryKey = useGetNextDueUserCardDataQueryKey();
  const getNextLessonQueryKey = useGetNextLessonQueryKey();
  const reviewRechargeQueryOptions = useReviewRechargeQueryOptions();
  const studiedGraphemesQueryOptions = useStudiedGraphemesQueryOptions();
  const pronouncedGraphemesQueryOptions = usePronouncedGraphemesQueryOptions();
  const levels = useLevels();

  const getNextDueUserCardDataQueryKeyRef = useLatest(getNextDueUserCardDataQueryKey);
  const getNextLessonQueryKeyRef = useLatest(getNextLessonQueryKey);
  const reviewRechargeQueryOptionsRef = useLatest(reviewRechargeQueryOptions);
  const studiedGraphemesQueryOptionsRef = useLatest(studiedGraphemesQueryOptions);
  const pronouncedGraphemesQueryOptionsRef = useLatest(pronouncedGraphemesQueryOptions);
  const levelsRef = useLatest(levels);

  return useMemo<LearnCardDestinationReads>(
    () => ({
      fetchNextDueUserCard: (override) =>
        queryClient.fetchQuery({
          queryFn: nextDueUserCardDataQueryFn,
          queryKey: getNextDueUserCardDataQueryKeyRef.current(override),
        }),
      fetchNextLesson: (override) =>
        queryClient.fetchQuery({
          queryFn: nextLessonQueryFn,
          queryKey: getNextLessonQueryKeyRef.current(override ?? {}),
        }),
      fetchPronouncedGraphemes: () =>
        queryClient.fetchQuery(pronouncedGraphemesQueryOptionsRef.current),
      fetchReviewRecharge: () => queryClient.fetchQuery(reviewRechargeQueryOptionsRef.current),
      fetchStudiedGraphemes: () => queryClient.fetchQuery(studiedGraphemesQueryOptionsRef.current),
      get levels() {
        return levelsRef.current;
      },
    }),
    [
      getNextDueUserCardDataQueryKeyRef,
      getNextLessonQueryKeyRef,
      levelsRef,
      pronouncedGraphemesQueryOptionsRef,
      reviewRechargeQueryOptionsRef,
      queryClient,
      studiedGraphemesQueryOptionsRef,
    ],
  );
}
